import React, { useEffect, useRef, useState } from 'react';
import Image from '../../components/Image/Image';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, A11y } from 'swiper';

SwiperCore.use([Navigation, A11y]);

function ImageSliderFullscreen({ isOpen, onClose, images, initialSlideIndex = 0, getAlt }) {
    const swiperRef = useRef(null);
    const closeButtonRef = useRef(null);
    const prevButtonRef = useRef(null);
    const nextButtonRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(initialSlideIndex);

    useEffect(() => {
        if (!isOpen) return;
        setActiveIndex(initialSlideIndex);
    }, [isOpen, initialSlideIndex]);

    useEffect(() => {
        if (!isOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handleKey = (e) => {
            if (e.key === 'Tab') {
                const current = document.activeElement;
                const prevBtn = prevButtonRef.current;
                const last = closeButtonRef.current;
                const nextBtn = nextButtonRef.current;
                if (!prevBtn || !nextBtn || !last) return;

                const prevDisabled = activeIndex === 0;
                const nextDisabled = activeIndex === images.length - 1;

                if (e.shiftKey) {
                    if (current === prevBtn) {
                        e.preventDefault();
                        last.focus();
                    } else if (current === nextBtn && prevDisabled) {
                        e.preventDefault();
                        last.focus();
                    }
                } else {
                    if (current === last) {
                        e.preventDefault();
                        if (prevDisabled) {
                            nextBtn.focus();
                        } else {
                            prevBtn.focus();
                        }
                    } else if (current === nextBtn && nextDisabled) {
                        e.preventDefault();
                        last.focus();
                    }
                }
                return;
            }

            if (!swiperRef.current) return;
            const swiper = swiperRef.current.swiper;

            if (e.key === 'Escape') {
                onClose();
                e.preventDefault();
            } else if (e.key === 'ArrowRight') {
                swiper.slideNext();
                e.preventDefault();
            } else if (e.key === 'ArrowLeft') {
                swiper.slidePrev();
                e.preventDefault();
            }
        };

        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose, activeIndex, images.length]);

    useEffect(() => {
        if (!isOpen) return;
        if (closeButtonRef.current) {
            closeButtonRef.current.focus();
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div
            className="image-slider-fullscreen"
            role="dialog"
            aria-modal="true"
            aria-label="Bildergalerie Vollbild"
            tabIndex={-1}
        >
            <div
                className="image-slider-fullscreen__content"
                onClick={(e) => e.target === e.currentTarget && onClose()}
                tabIndex={-1}
                onKeyDown={(e) => {
                    if (!swiperRef.current) return;
                    const swiper = swiperRef.current.swiper;
                    if (e.key === "ArrowRight") {
                        swiper.slideNext();
                        e.preventDefault();
                    }
                    if (e.key === "ArrowLeft") {
                        swiper.slidePrev();
                        e.preventDefault();
                    }
                }}
                aria-label="Bildergalerie Vollbild. Mit den Pfeiltasten navigieren."
                role="region"
                aria-roledescription="Karussell"
            >
                <Swiper
                    ref={swiperRef}
                    initialSlide={initialSlideIndex}
                    spaceBetween={0}
                    slidesPerView={1}
                    navigation={{
                        prevEl: '.image-slider-fullscreen .swiper-button-prev',
                        nextEl: '.image-slider-fullscreen .swiper-button-next',
                    }}
                    onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
                    centeredSlides
                    a11y={{
                        prevSlideMessage: 'Vorheriger slide',
                        nextSlideMessage: 'Nächster slide',
                    }}
                    className="uk-height-1-1"
                >
                    {images.map((src, i) => (
                        <SwiperSlide
                            key={i}
                            className="uk-flex uk-flex-middle uk-flex-center"
                            role="group"
                            aria-roledescription="Bild"
                            aria-label={`Bild ${i + 1} von ${images.length}`}
                        >
                            <Image
                                src={src}
                                alt={getAlt ? `${getAlt(i) ?? 'Produktbild'} ${i + 1}` : `Bild ${i + 1}`}
                                className="uk-width-auto uk-height-1-1"
                                resizeWidth={800}
                                resizeHeight={800}
                                fit="inside"
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>

                <div className="image-slider-fullscreen__bottom-bar" role="toolbar" aria-label="Bildergalerie Steuerung">
                    <span
                        className="image-slider-fullscreen__counter"
                        aria-live="polite"
                        aria-atomic="true"
                        role="status"
                    >
                        {activeIndex + 1} von {images.length}
                    </span>
                    <div
                        className="image-slider-fullscreen__nav-container"
                        role="group"
                        aria-label="Navigation"
                    >
                        <button
                            ref={prevButtonRef}
                            type="button"
                            className="swiper-button-prev"
                            aria-label="Vorheriger slide"
                        />
                        <button
                            ref={nextButtonRef}
                            type="button"
                            className="swiper-button-next"
                            aria-label="Nächster slide"
                        />
                    </div>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        onClick={onClose}
                        className="image-slider-fullscreen__close-button"
                        aria-label="Schließen"
                    >
                        <i data-uk-icon="close" aria-hidden="true"></i>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ImageSliderFullscreen;
